import React, { useState, useEffect } from 'react';
import { clickable } from '../../../utils/a11y';
import { Link, useNavigate } from 'react-router-dom';
import './ContractorDashboard.css';
import allSafeLogo from '../../../assets/cloudsafe-logo.png';
import { 
  FaClipboardList,
  FaFileAlt,
  FaTasks,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaUserCircle,
  FaBell,
  FaSearch,
  FaUsers,
  FaBuilding,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaChartLine,
  FaCertificate,
  FaEye,
  FaUpload,
  FaPlay,
  FaBars,
  FaTimes
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole } from '../../../utils/roleUtils';
import { clearToken } from '../../../utils/auth';
import { getContractorTasks, updateContractorTaskStatus } from '../../../services/contractorService';
import { getApiEndpoint } from '../../../config/api';

const ContractorTasks = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filter, setFilter] = useState('all'); // all, pending, in_progress, completed
  const [sortBy, setSortBy] = useState('dueDate'); // dueDate, priority, status
  const [loading, setLoading] = useState(true);

  const mapTaskFromApi = (t) => {
    const today = new Date().toISOString().split('T')[0];
    const isOverdue = t.due_date && t.due_date < today && t.status !== 'completed' && t.status !== 'cancelled';
    const statusDisplay = isOverdue ? 'Overdue' : (t.status || 'pending').replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase());
    return {
      id: t.id,
      title: t.title,
      property: t.property_name || `Property #${t.property_id}`,
      propertyAddress: t.property_address || '',
      client: t.client || 'Client',
      dueDate: t.due_date,
      priority: (t.priority || 'medium').replace(/\b\w/g, c => c.toUpperCase()),
      status: statusDisplay,
      estimatedHours: 4,
      description: t.description || '',
      assignedDate: t.created_at?.split('T')[0],
      category: 'Task',
      notes: t.notes,
      _raw: t
    };
  };

  useEffect(() => {
    const userRole = getUserRole();
    if (userRole !== 'contractor') {
      toast.error('Access denied');
      navigate('/login');
      return;
    }
    fetchTasksData();
  }, [navigate]);

  const fetchTasksData = async () => {
    setLoading(true);
    try {
      const profileResponse = await fetch(getApiEndpoint('/api/users/profile'), {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        setUserName(`${profileData.firstName} ${profileData.lastName}`);
      }
      const taskRows = await getContractorTasks({ limit: 100 });
      setTasks(taskRows.map(mapTaskFromApi));
    } catch (error) {
      console.error('Error fetching tasks data:', error);
      toast.error('Failed to load tasks');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    clearToken();
    localStorage.removeItem('userRole');
    navigate('/login');
    toast.success('Logged out successfully');
  };

  // Quick Action Handlers
  const handleTrackTime = () => {
    toast.info('Time tracking feature coming soon!');
    // TODO: Open time tracking modal or navigate to time tracking page
    console.log('Track Time clicked');
  };

  const handleCreateReport = () => {
    toast.info('Create Report functionality coming soon!');
    // TODO: Navigate to create report form or open modal
    console.log('Create Report clicked');
  };

  const handleUploadPhotos = () => {
    // Create a hidden file input to trigger photo upload
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length > 0) {
        toast.success(`Selected ${files.length} photo(s) for upload`);
        // TODO: Implement actual photo upload logic
        console.log('Photos selected for upload:', files);
      }
    };
    input.click();
  };

  const handleNewReport = () => {
    toast.info('New Report functionality coming soon!');
    // TODO: Navigate to new report creation page
    console.log('New Report clicked');
  };

  const handleStartTask = async (taskId) => {
    try {
      await updateContractorTaskStatus(taskId, 'in_progress');
      setTasks(prev => prev.map(task => task.id === taskId ? { ...task, status: 'In Progress' } : task));
      toast.success('Task started');
    } catch (e) {
      toast.error(e?.message || 'Failed to start task');
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await updateContractorTaskStatus(taskId, 'completed');
      setTasks(prev => prev.map(task => task.id === taskId ? { ...task, status: 'Completed', completedDate: new Date().toISOString().split('T')[0] } : task));
      toast.success('Task completed');
    } catch (e) {
      toast.error(e?.message || 'Failed to complete task');
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'completed': return <FaCheckCircle />;
      case 'in progress': return <FaClock />;
      case 'pending': return <FaExclamationTriangle />;
      case 'overdue': return <FaExclamationTriangle />;
      default: return <FaTasks />;
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (filter === 'all') return true;
    const s = task.status.toLowerCase().replace(' ', '_');
    if (filter === 'overdue') return s === 'overdue';
    return s === filter;
  });

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    switch (sortBy) {
      case 'dueDate':
        return new Date(a.dueDate) - new Date(b.dueDate);
      case 'priority':
        const priorityOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      case 'status':
        return a.status.localeCompare(b.status);
      default:
        return 0;
    }
  });

  return (
    <div className="contractor-dashboard">
      <header className="contractor-header">
        <button className="mobile-menu-toggle" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="Toggle menu">
          {sidebarOpen ? <FaTimes /> : <FaBars />}
        </button>
        <img src={allSafeLogo} alt="CloudSafe" className="header-logo" />
        <div className="header-right">
          <nav className="nav-links">
            <Link to="/contractor/dashboard" className="nav-link">Dashboard</Link>
            <Link to="/contractor/tasks" className="nav-link active">My Tasks</Link>
            <Link to="/contractor/inspections" className="nav-link">Inspections</Link>
            <Link to="/contractor/reports" className="nav-link">Reports</Link>
          </nav>
          <div className="header-icons">
            <div className="icon"><FaSearch /></div>
            <div className="icon"><FaBell /></div>
            <div className="user-avatar-container" aria-haspopup="menu" aria-expanded={showUserMenu} {...clickable(() => setShowUserMenu(!showUserMenu), { label: 'User menu' })}>
              <FaUserCircle />
              {showUserMenu && (
                <div className="user-menu">
                  <Link to="/contractor/profile" onClick={() => setShowUserMenu(false)}>Profile</Link>
                  <button onClick={handleLogout}>Logout</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="contractor-layout">
        <div className={`sidebar-overlay ${sidebarOpen ? 'visible' : ''}`} onClick={() => setSidebarOpen(false)} />
        <aside className={`contractor-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-section">
            <h3>Navigation</h3>
            <nav className="sidebar-nav">
              <Link to="/contractor/dashboard" className="nav-item" onClick={() => setSidebarOpen(false)}>
                <FaChartLine /><span>Dashboard</span>
              </Link>
              <Link to="/contractor/tasks" className="nav-item active" onClick={() => setSidebarOpen(false)}>
                <FaTasks /><span>My Tasks</span>
              </Link>
              <Link to="/contractor/inspections" className="nav-item" onClick={() => setSidebarOpen(false)}>
                <FaClipboardList /><span>Inspections</span>
              </Link>
            </nav>
          </div>
          <div className="sidebar-section">
            <h3>Quick Actions</h3>
            <nav className="sidebar-nav">
              <button className="nav-item" onClick={() => { handleTrackTime(); setSidebarOpen(false); }}>
                <FaClock /><span>Track Time</span>
              </button>
              <button className="nav-item" onClick={() => { handleCreateReport(); setSidebarOpen(false); }}>
                <FaFileAlt /><span>Create Report</span>
              </button>
              <button className="nav-item" onClick={() => { handleUploadPhotos(); setSidebarOpen(false); }}>
                <FaUpload /><span>Upload Photos</span>
              </button>
            </nav>
          </div>
        </aside>

        <main className="contractor-content">
          <div className="con-page-header">
            <div>
              <h1>My Tasks</h1>
              <p className="con-page-subtitle">Manage your assigned tasks and work orders</p>
            </div>
            <div className="header-actions">
              <button className="btn-secondary" onClick={handleTrackTime}><FaClock /> Track Time</button>
              <button className="btn-primary" onClick={handleNewReport}><FaFileAlt /> New Report</button>
            </div>
          </div>

          <div className="stats-grid">
            <div className="stat-card pending">
              <div className="stat-icon"><FaExclamationTriangle /></div>
              <div className="stat-content">
                <h3>Pending</h3>
                <p className="stat-number">{tasks.filter(t => t.status === 'Pending').length}</p>
              </div>
            </div>
            <div className="stat-card completed">
              <div className="stat-icon"><FaClock /></div>
              <div className="stat-content">
                <h3>In Progress</h3>
                <p className="stat-number">{tasks.filter(t => t.status === 'In Progress').length}</p>
              </div>
            </div>
            <div className="stat-card clients">
              <div className="stat-icon"><FaCheckCircle /></div>
              <div className="stat-content">
                <h3>Completed</h3>
                <p className="stat-number">{tasks.filter(t => t.status === 'Completed').length}</p>
              </div>
            </div>
            <div className="stat-card overdue">
              <div className="stat-icon"><FaExclamationTriangle /></div>
              <div className="stat-content">
                <h3>Overdue</h3>
                <p className="stat-number">{tasks.filter(t => t.status === 'Overdue').length}</p>
              </div>
            </div>
          </div>

          <div className="task-controls">
            <div className="filter-controls">
              <label>Status:</label>
              <select value={filter} onChange={(e) => setFilter(e.target.value)}>
                <option value="all">All Tasks</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
            <div className="sort-controls">
              <label>Sort:</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="dueDate">Due Date</option>
                <option value="priority">Priority</option>
                <option value="status">Status</option>
              </select>
            </div>
          </div>

          <div className="tasks-list">
            {loading ? (
              <div className="staff-loading" style={{ padding: '2rem', textAlign: 'center' }}>Loading tasks...</div>
            ) : sortedTasks.length === 0 ? (
              <div className="con-empty-state" style={{ marginTop: '1rem' }}>
                <FaClipboardList className="empty-icon" />
                <h3>No tasks assigned</h3>
                <p>When tasks are assigned to you they will appear here.</p>
              </div>
            ) : sortedTasks.map(task => (
              <div key={task.id} className="task-card">
                <div className="task-header">
                  <div className="task-info">
                    <h3>{task.title}</h3>
                    <div className="task-meta">
                      <span className="property-name"><FaBuilding /> {task.property}</span>
                      <span className="client-name"><FaUsers /> {task.client}</span>
                    </div>
                  </div>
                  <div className="task-status-info">
                    <span className={`priority ${task.priority.toLowerCase()}`}>{task.priority}</span>
                    <span className={`status ${task.status.toLowerCase().replace(' ', '-')}`}>
                      {getStatusIcon(task.status)} {task.status}
                    </span>
                  </div>
                </div>

                <div className="task-description"><p>{task.description}</p></div>

                <div className="task-details">
                  <div className="detail-grid">
                    <div className="detail-item"><FaMapMarkerAlt className="icon" /><span>{task.propertyAddress}</span></div>
                    <div className="detail-item"><FaCalendarAlt className="icon" /><span>Due: {task.dueDate}</span></div>
                    <div className="detail-item"><FaClock className="icon" /><span>Est: {task.estimatedHours}h</span></div>
                    <div className="detail-item"><FaCertificate className="icon" /><span>{task.category}</span></div>
                  </div>
                </div>

                {task.status === 'In Progress' && (
                  <div className="task-progress">
                    <div className="progress-info">
                      <span>Progress: {task.completedHours || 0}/{task.estimatedHours}h</span>
                    </div>
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${((task.completedHours || 0) / task.estimatedHours) * 100}%` }} />
                    </div>
                  </div>
                )}

                <div className="task-actions">
                  {task.status === 'Pending' && (
                    <button className="action-btn start" onClick={() => handleStartTask(task.id)}><FaPlay /> Start Task</button>
                  )}
                  {task.status === 'In Progress' && (
                    <>
                      <button className="action-btn"><FaClock /> Log Time</button>
                      <button className="action-btn complete" onClick={() => handleCompleteTask(task.id)}><FaCheckCircle /> Complete</button>
                    </>
                  )}
                  <button className="action-btn"><FaUpload /> Photos</button>
                  <button className="action-btn"><FaEye /> Details</button>
                  <button className="action-btn"><FaMapMarkerAlt /> Directions</button>
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ContractorTasks; 